import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router";
import axios from 'axios';

import { AppContext } from '../../../../context.js';

import Loading from '../../../components/loading.js';
import Helmet from "../../../components/helmet.js";
import { NanosecondsToHHMM } from '../../../../utils/datetime.js';

function ReferralsStatistics() {
  const context = useContext(AppContext);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState([]);
  const [expandedTemplates, setExpandedTemplates] = useState([]);

  useEffect(() => {
    let u = context.addSpiner();
    axios.post('/api/admin/analytics/referrals/statistics', {})
      .then((response) => {
        setStatistics(response.data);
        setLoading(false);
      })
      .catch((e) => {
        context.addError(e);
        context.removeSpiner(u);
      })
      .finally(() => {
        context.removeSpiner(u);
      });
  }, []);

  function toggleTemplate(templateID) {
    if (expandedTemplates.includes(templateID)) {
      setExpandedTemplates(expandedTemplates.filter(id => id !== templateID));
    } else {
      setExpandedTemplates([...expandedTemplates, templateID]);
    }
  }

  function renderTemplates() {
    if (!statistics || statistics.length === 0) {
      return (
        <div className="card mb-2">
          <div className="card-header sm">
            <div className="typo-normal-150">No referral templates available.</div>
          </div>
        </div>
      );
    }

    return statistics.map((template, index) => {
      const isExpanded = expandedTemplates.includes(template.id);

      return (
        <div key={index} className="card mb-2">
          <div className="card-header clickable sm" onClick={() => toggleTemplate(template.id)}>
            <div className="row">
              <div className="col-xs-2">
                <div className="typo-strong-175">{template.title}</div>
              </div>
              <div className="col-xs-8">
                <div className="row">
                  <div className="col-xs-2 text-center">
                    <div className="typo-strong-175">{template.total}</div>
                  </div>
                  <div className="col-xs-2 text-center">
                    <div className="typo-strong-175">{template.toDo}</div>
                  </div>
                  <div className="col-xs-2 text-center">
                    <div className="typo-strong-175">{template.inProgress}</div>
                  </div>
                  <div className="col-xs-2 text-center">
                    <div className="typo-strong-175">{template.paused}</div>
                  </div>
                  <div className="col-xs-2 text-center">
                    <div className="typo-strong-175">{template.completed}</div>
                  </div>
                  <div className="col-xs-2 text-center">
                    <div className="typo-strong-175">{template.canceled}</div>
                  </div>
                </div>
              </div>
              <div className="col-xs-2 text-end">
                <div className="typo-strong-175">{template.lifetime > 0 ? NanosecondsToHHMM(template.lifetime) + ' h' : '-'}</div>
              </div>
            </div>
          </div>
          {isExpanded && renderTemplatePartners(template.id, template.partners)}
        </div>
      );
    });
  }

  function renderTemplatePartners(templateID, partners) {
    return partners.filter(partner => partner.total > 0).sort((a, b) => b.total - a.total).map((partner, index) => {
      let url = `/patients?tab=${templateID}`;
      if (partner.selfReferral) {
        url += `&selfReferral=true`;
      } else {
        url += `&partnerID=${partner.partnerID}`;
      }

      return (
        <div key={index} className={"card-header clickable sm " + (partners.length === index + 1 ? "last" : "")} onClick={() => navigate(url)}>
          <div className="row">
            <div className="col-xs-2">
              <div className="typo-strong-150">{partner.selfReferral ? "Self Referral" : partner.partnerName}</div>
            </div>
            <div className="col-xs-8">
              <div className="row">
                <div className="col-xs-2 text-center">
                  <div className="typo-strong-150">{partner.total}</div>
                </div>
                <div className="col-xs-2 text-center">
                  <div className="typo-normal-150">{partner.toDo}</div>
                </div>
                <div className="col-xs-2 text-center">
                  <div className="typo-normal-150">{partner.inProgress}</div>
                </div>
                <div className="col-xs-2 text-center">
                  <div className="typo-normal-150">{partner.paused}</div>
                </div>
                <div className="col-xs-2 text-center">
                  <div className="typo-normal-150">{partner.completed}</div>
                </div>
                <div className="col-xs-2 text-center">
                  <div className="typo-normal-150">{partner.canceled}</div>
                </div>
              </div>
            </div>
            <div className="col-xs-2 text-end">
              <div className="typo-normal-150">{partner.lifetime > 0 ? NanosecondsToHHMM(partner.lifetime) + ' h' : '-'}</div>
            </div>
          </div>
        </div>
      );
    });
  }

  if (loading) {
    return (
      <>
        <Helmet title="Analytics | Referral Analytics Dashboard" />
        <Loading />
      </>
    );
  }

  return (
    <>
      <Helmet title="Analytics | Referral Analytics Dashboard" />

      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="">
                <div className="typo-hero-small">Referral Analytics Dashboard</div>
                <div className="typo-normal-175">Track referral processing efficiency and status distribution across all referral templates. Monitor workflow performance and identify areas for improvement in patient referral management</div>
              </div>
              <div className="w-25 text-end">
                {/* <div className="item typo-normal-150 ms-2" onClick={() => navigate('/patient/create')}><FilePlus height={16} width={16} background={'none'} fill={'grey'} /> Create Patient</div> */}
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12">

            <div className="card mb-2">
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-2">
                    <div className="typo-strong-150">Template</div>
                  </div>
                  <div className="col-xs-8">
                    <div className="row">
                      <div className="col-xs-2 text-center">
                        <div className="typo-strong-150">Total</div>
                      </div>
                      <div className="col-xs-2 text-center">
                        <div className="typo-strong-150">To Do</div>
                      </div>
                      <div className="col-xs-2 text-center">
                        <div className="typo-strong-150">In Progress</div>
                      </div>
                      <div className="col-xs-2 text-center">
                        <div className="typo-strong-150">Paused</div>
                      </div>
                      <div className="col-xs-2 text-center">
                        <div className="typo-strong-150">Completed</div>
                      </div>
                      <div className="col-xs-2 text-center">
                        <div className="typo-strong-150">Canceled</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-xs-2 text-end">
                    <div className="typo-strong-150">Avg. Lifetime</div>
                  </div>
                </div>
              </div>
            </div>

            {renderTemplates()}

          </div>
        </div>
      </div>
      
    </>
  );
}

export default ReferralsStatistics;
